import { useEffect, useRef, type CSSProperties } from "react";
import { MessageStack, ProductMark, Thinking } from "@fwf/fwfa-design-system/react";
import { Message } from "./Message";
import { PermissionDialog } from "./PermissionDialog";
import { ToolCallActivity } from "./ToolCallActivity";
import { ThinkingIndicator } from "./ThinkingIndicator";
import type { ActiveToolCall, ChatMessage, PermissionRequest } from "@/types";

interface Props {
  messages: ChatMessage[];
  isStreaming: boolean;
  isThinking: boolean;
  thinkingContent: string;
  pendingPermission: PermissionRequest | null;
  activeTool: string | null;
  activeToolCall: ActiveToolCall | null;
  completedToolCalls: Record<string, ActiveToolCall>;
  onPermissionResponse: (optionId: string | null) => void;
  onAlwaysApprovePermission: () => void;
  scrollBehavior?: ScrollBehavior;
  isLoading?: boolean;
}

export function MessageList({
  messages,
  isStreaming,
  isThinking,
  thinkingContent,
  pendingPermission,
  activeTool,
  activeToolCall,
  completedToolCalls,
  onPermissionResponse,
  onAlwaysApprovePermission,
  scrollBehavior = "smooth",
  isLoading = false,
}: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: scrollBehavior });
  }, [messages, pendingPermission, activeTool, isThinking, thinkingContent, activeToolCall, scrollBehavior]);

  if (messages.length === 0) {
    if (isLoading) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 rounded-full border-2 border-fwf-yellow/20 border-t-gold animate-spin" />
        </div>
      );
    }
    return (
      <div className="flex-1 flex items-center justify-center px-8 py-10">
        <div className="text-center max-w-2xl animate-fade-in">
          {/* Monogram alone, no wordmark beside it, so the icon variant is right
              here. `--pm-icon-size` is the DS's own sizing hook; the tile art and
              the light/dark swap come from the product tokens. */}
          <div className="inline-flex items-center justify-center mb-6">
            <ProductMark
              variant="icon"
              style={{ "--pm-icon-size": "5rem" } as CSSProperties}
            />
          </div>
          <h2 className="font-display text-[2.75rem] leading-none font-semibold tracking-[-0.03em] text-navy dark:text-white mb-4 sm:text-[3.25rem]">
            Ready to build
          </h2>
          <p className="mx-auto max-w-xl font-body text-base text-stone-500 dark:text-stone-400 leading-8 sm:text-[1.125rem]">
            Your AI coding agent is connected. Start with a task, paste a file, or use / to jump into a command.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="ph-no-capture flex-1 overflow-y-auto px-4 py-4" data-private="workspace-transcript">
      {/* The DS owns the column: 46rem centred with a --fwfa-space-4 gap between
          turns. Children no longer carry their own bottom margins — the grid gap
          is the one spacing authority, so a turn can never set its own rhythm. */}
      <MessageStack>
        {messages.map((msg) => {
          // Skip the running tool message — ToolCallActivity renders it with xterm
          if (
            msg.role === "tool"
            && msg.toolStatus === "running"
            && activeToolCall
            && msg.id === `tool:${activeToolCall.toolCallId}`
          ) {
            return null;
          }
          // Completed tool calls with output: render with xterm instead of plain text
          if (msg.role === "tool" && msg.toolStatus !== "running") {
            const callId = msg.id.replace(/^tool:/, "");
            const completed = completedToolCalls[callId];
            if (completed) {
              return <ToolCallActivity key={msg.id} toolCall={completed} />;
            }
          }
          return <Message key={msg.id} message={msg} />;
        })}

        {isThinking && <ThinkingIndicator content={thinkingContent} />}

        {activeToolCall && <ToolCallActivity toolCall={activeToolCall} />}

        {pendingPermission && (
          <PermissionDialog
            request={pendingPermission}
            onResponse={onPermissionResponse}
            onAlwaysApprove={onAlwaysApprovePermission}
          />
        )}

        {/* Waiting for the first token. The DS indicator is three muted dots
            aligned like an assistant turn, no bubble and no yellow — the old
            gold dots in a navy pill spent an accent on the most repeated state
            in the whole thread. */}
        {isStreaming && !activeTool && !isThinking && <Thinking />}
      </MessageStack>

      <div ref={bottomRef} />
    </div>
  );
}
